import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@reflex/ui/client/http';
import { useOrgKey } from '@reflex/ui/client/context';
import type {
  CreateFlowReplay,
  CreateFlowReplayFromRun,
  SavedFlowReplay,
  UpdateFlowReplay,
} from '../shared/replay.js';

type OrgKey = ReturnType<typeof useOrgKey>;
const flowReplaysKey = (org: OrgKey) => ['flow-replays', org] as const;
const flowReplayKey = (org: OrgKey, id: string | undefined) => ['flow-replay', org, id] as const;

export const flowReplaysApi = {
  list: () => request<SavedFlowReplay[]>('/flow-replays'),
  get: (id: string) => request<SavedFlowReplay>(`/flow-replays/${encodeURIComponent(id)}`),
  create: (data: CreateFlowReplay) =>
    request<SavedFlowReplay>('/flow-replays', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createFromRun: (data: CreateFlowReplayFromRun) =>
    request<SavedFlowReplay>('/flow-replays/from-run', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: UpdateFlowReplay) =>
    request<SavedFlowReplay>(`/flow-replays/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  setShared: (id: string, shared: boolean) =>
    request<SavedFlowReplay>(`/flow-replays/${encodeURIComponent(id)}/share`, {
      method: shared ? 'POST' : 'DELETE',
    }),
  remove: (id: string) =>
    request<{ ok: boolean }>(`/flow-replays/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export function useFlowReplays() {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: flowReplaysKey(orgKey),
    queryFn: () => flowReplaysApi.list(),
  });
}

export function useFlowReplay(id: string | undefined) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: flowReplayKey(orgKey, id),
    queryFn: () => flowReplaysApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateFlowReplay() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (data: CreateFlowReplay) => flowReplaysApi.create(data),
    onSuccess: (replay) => {
      qc.setQueryData<SavedFlowReplay>(flowReplayKey(orgKey, replay.id), replay);
      qc.invalidateQueries({ queryKey: flowReplaysKey(orgKey) });
    },
  });
}

export function useCreateFlowReplayFromRun() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (data: CreateFlowReplayFromRun) => flowReplaysApi.createFromRun(data),
    onSuccess: (replay) => {
      qc.setQueryData<SavedFlowReplay>(flowReplayKey(orgKey, replay.id), replay);
      qc.invalidateQueries({ queryKey: flowReplaysKey(orgKey) });
    },
  });
}

export function useUpdateFlowReplay() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFlowReplay }) =>
      flowReplaysApi.update(id, data),
    onSuccess: (replay) => {
      qc.setQueryData<SavedFlowReplay>(flowReplayKey(orgKey, replay.id), replay);
      qc.invalidateQueries({ queryKey: flowReplaysKey(orgKey) });
    },
  });
}

/**
 * Publish or retract a replay's public share link. Server-gated to the
 * captured run's user (the replay's creator for run-less rows) or an org
 * manager; other members get a 403.
 */
export function useSetFlowReplayShared() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, shared }: { id: string; shared: boolean }) =>
      flowReplaysApi.setShared(id, shared),
    onSuccess: (replay) => {
      qc.setQueryData<SavedFlowReplay>(flowReplayKey(orgKey, replay.id), replay);
      qc.invalidateQueries({ queryKey: flowReplaysKey(orgKey) });
    },
  });
}

export function useDeleteFlowReplay() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => flowReplaysApi.remove(id),
    onSuccess: (_result, id) => {
      qc.removeQueries({ queryKey: flowReplayKey(orgKey, id) });
      qc.invalidateQueries({ queryKey: flowReplaysKey(orgKey) });
    },
  });
}
